/**
 * Templates pour le module Budget Prévisionnel 5 ans
 *
 * Ce fichier contient les templates pré-configurés,
 * notamment le template "Fer Ensemble" pour les écoles de production
 */

import { supabase } from '../lib/supabase';

/**
 * Applique le template Fer Ensemble à un scénario existant
 */
export async function applyFerEnsembleTemplate(scenarioId: string) {
  try {
    // 1. Créer les dimensions (CAP1, CAP2, POSTCAP)
    const dimensionsData = [
      { code: 'CAP1', label: 'Élèves CAP 1ère année', unit_name: 'élèves' },
      { code: 'CAP2', label: 'Élèves CAP 2ème année', unit_name: 'élèves' },
      { code: 'POSTCAP', label: 'Élèves Post-CAP', unit_name: 'élèves' }
    ];

    const { data: dimensions, error: dimError } = await supabase
      .from('forecast_dimensions')
      .insert(
        dimensionsData.map(d => ({
          scenario_id: scenarioId,
          ...d
        }))
      )
      .select();

    if (dimError) throw dimError;
    if (!dimensions || dimensions.length !== 3) throw new Error('Failed to create dimensions');

    const cap1Dim = dimensions.find(d => d.code === 'CAP1')!;
    const cap2Dim = dimensions.find(d => d.code === 'CAP2')!;
    const postcapDim = dimensions.find(d => d.code === 'POSTCAP')!;

    // 2. Créer les valeurs des dimensions pour 5 ans
    const dimensionValuesData = [
      // CAP1: 11, 12, 13, 16, 16
      { dimension_id: cap1Dim.id, year_index: 1, value: 11 },
      { dimension_id: cap1Dim.id, year_index: 2, value: 12 },
      { dimension_id: cap1Dim.id, year_index: 3, value: 13 },
      { dimension_id: cap1Dim.id, year_index: 4, value: 16 },
      { dimension_id: cap1Dim.id, year_index: 5, value: 16 },
      // CAP2: 11, 12, 13, 13, 16
      { dimension_id: cap2Dim.id, year_index: 1, value: 11 },
      { dimension_id: cap2Dim.id, year_index: 2, value: 12 },
      { dimension_id: cap2Dim.id, year_index: 3, value: 13 },
      { dimension_id: cap2Dim.id, year_index: 4, value: 13 },
      { dimension_id: cap2Dim.id, year_index: 5, value: 16 },
      // POSTCAP: 0, 8, 8, 8, 8
      { dimension_id: postcapDim.id, year_index: 1, value: 0 },
      { dimension_id: postcapDim.id, year_index: 2, value: 8 },
      { dimension_id: postcapDim.id, year_index: 3, value: 8 },
      { dimension_id: postcapDim.id, year_index: 4, value: 8 },
      { dimension_id: postcapDim.id, year_index: 5, value: 8 }
    ];

    const { error: valuesError } = await supabase
      .from('forecast_dimension_values')
      .insert(
        dimensionValuesData.map(dv => ({
          scenario_id: scenarioId,
          ...dv
        }))
      );

    if (valuesError) throw valuesError;

    // 3. Créer les lignes de charges (Personnel)
    const personnelLines = [
      {
        type: 'expense',
        section: 'Personnel',
        label: 'Directeur',
        rule_type: 'fixed',
        base_amount: 45000,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 1
      },
      {
        type: 'expense',
        section: 'Personnel',
        label: 'Maître professionnel 1',
        rule_type: 'fixed',
        base_amount: 35000,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 2
      },
      {
        type: 'expense',
        section: 'Personnel',
        label: 'Maître professionnel 2',
        rule_type: 'fixed',
        base_amount: 35000,
        active_from_year: 2,
        active_to_year: 5,
        sort_order: 3
      },
      {
        type: 'expense',
        section: 'Personnel',
        label: 'Maître professionnel 3',
        rule_type: 'threshold',
        base_amount: 0,
        dimension_id: cap1Dim.id,
        threshold_unit_value: 16,
        threshold_amount: 35000,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 4
      },
      {
        type: 'expense',
        section: 'Personnel',
        label: 'Formateur technique',
        rule_type: 'fixed',
        base_amount: 30000,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 5
      },
      {
        type: 'expense',
        section: 'Personnel',
        label: 'Charges sociales (estimation 45%)',
        rule_type: 'growth',
        base_amount: 63000,
        growth_rate: 2,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 6
      }
    ];

    // 4. Créer les lignes de charges (Frais généraux)
    const fraisGenerauxLines = [
      {
        type: 'expense',
        section: 'Frais généraux',
        label: 'Location bâtiment',
        rule_type: 'fixed',
        base_amount: 24000,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 10
      },
      {
        type: 'expense',
        section: 'Frais généraux',
        label: 'Matières premières et fournitures',
        rule_type: 'growth',
        base_amount: 15000,
        growth_rate: 3,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 11
      },
      {
        type: 'expense',
        section: 'Frais généraux',
        label: 'Assurances',
        rule_type: 'fixed',
        base_amount: 5000,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 12
      },
      {
        type: 'expense',
        section: 'Frais généraux',
        label: 'Communication et marketing',
        rule_type: 'fixed',
        base_amount: 3000,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 13
      }
    ];

    // 5. Créer les lignes de produits (Autofinancement)
    const autofinancementLines = [
      {
        type: 'revenue',
        section: 'Autofinancement',
        label: 'Production élèves (ventes)',
        rule_type: 'growth',
        base_amount: 35000,
        growth_rate: 5,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 20
      },
      {
        type: 'revenue',
        section: 'Autofinancement',
        label: 'Prestations de service',
        rule_type: 'growth',
        base_amount: 12000,
        growth_rate: 8,
        active_from_year: 2,
        active_to_year: 5,
        sort_order: 21
      }
    ];

    // 6. Créer les lignes de produits (Subventions publiques)
    const subventionsPubliquesLines = [
      {
        type: 'revenue',
        section: 'Subventions publiques',
        label: 'Région - Formation professionnelle',
        rule_type: 'per_unit',
        base_amount: 0,
        dimension_id: cap1Dim.id,
        amount_per_unit: 8000,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 30
      },
      {
        type: 'revenue',
        section: 'Subventions publiques',
        label: 'État - Ministère du Travail',
        rule_type: 'fixed',
        base_amount: 50000,
        active_from_year: 1,
        active_to_year: 5,
        sort_order: 31
      }
    ];

    // 7. Créer les lignes de produits (Fondations et mécénat)
    const fondationsLines = [
      {
        type: 'revenue',
        section: 'Fondations et mécénat',
        label: 'Fondation de France',
        rule_type: 'fixed',
        base_amount: 30000,
        active_from_year: 1,
        active_to_year: 3,
        sort_order: 40
      },
      {
        type: 'revenue',
        section: 'Fondations et mécénat',
        label: 'Fondation Apprentis d\'Auteuil',
        rule_type: 'fixed',
        base_amount: 25000,
        active_from_year: 1,
        active_to_year: 2,
        sort_order: 41
      },
      {
        type: 'revenue',
        section: 'Fondations et mécénat',
        label: 'Mécénat d\'entreprise',
        rule_type: 'growth',
        base_amount: 10000,
        growth_rate: 10,
        active_from_year: 2,
        active_to_year: 5,
        sort_order: 42
      }
    ];

    // 8. Créer les lignes de produits (Amorçage)
    const amorcageLines = [
      {
        type: 'revenue',
        section: 'Amorçage',
        label: 'Apport fondateurs',
        rule_type: 'fixed',
        base_amount: 20000,
        active_from_year: 1,
        active_to_year: 1,
        sort_order: 50
      }
    ];

    // Combiner toutes les lignes
    const allLines = [
      ...personnelLines,
      ...fraisGenerauxLines,
      ...autofinancementLines,
      ...subventionsPubliquesLines,
      ...fondationsLines,
      ...amorcageLines
    ];

    const { error: linesError } = await supabase
      .from('forecast_line_items')
      .insert(
        allLines.map(line => ({
          scenario_id: scenarioId,
          ...line
        }))
      );

    if (linesError) throw linesError;

    return { success: true };
  } catch (error) {
    console.error('Error applying Fer Ensemble template:', error);
    throw error;
  }
}
